import React, {
  useEffect,
  useState,
  useCallback,
  type PropsWithChildren,
} from "react";
import {
  Button,
  Spinner,
  Table,
  Form,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faDownload } from "@fortawesome/free-solid-svg-icons";
import { useDebounce } from "../../utils/useDebounce";
import { api } from "../../utils/api";
import { humanFileSize } from "../../utils/helper";
import {
  useAuthorization,
  type AuthorizationHookResult,
  type UserType,
} from "../../authorization/useAuthorization";
import { ErrorMessage } from "../../authorization/ErrorMessage";
import type z from "zod";
import { schemas } from "../../api/generated";
import { AuthorizationErrorMessage } from "../../authorization/AuthorizationErrorMessage";

type File = z.infer<typeof schemas.File>;

type AuthorizedComponentProps = PropsWithChildren<{
  userTypes: UserType[];
  element: React.ComponentType<{ token: string; userId: string }>;
  unauthorizedElement?: React.ComponentType<AuthorizationHookResult>;
}>;

function AuthorizedComponent({
  userTypes,
  element,
  unauthorizedElement,
  ...extraProps
}: AuthorizedComponentProps) {
  const auth = useAuthorization({ userTypes }); // Adjust user types as needed

  if (!auth.authorized) {
    if (unauthorizedElement) {
      const UnathorizedElement = unauthorizedElement;
      return <UnathorizedElement {...auth} />;
    } else {
      return <></>; // Render nothing if not authorized
    }
  }

  const token = auth.token;
  const userId = auth.userId;

  const ChildElement = element;
  return <ChildElement token={token} userId={userId} {...extraProps} />;
}

function UserHome({ token, userId }: { token: string; userId: string }) {
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const getAllFiles = useCallback(
    (searchText?: string) => {
      api
        .getFilesAssignedToUser(token, userId, searchText)
        .then((data) => {
          setFiles(data.files);
          setErrorMessage(""); // Clear any previous error message
        })
        .catch((error) => {
          console.error("Error fetching files:", error);
          setFiles([]);
          setErrorMessage("Failed to fetch files.");
        });
    },
    [token, userId]
  );

  useEffect(() => {
    getAllFiles();
  }, [getAllFiles]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      getAllFiles(debouncedSearchTerm);
    }
  }, [getAllFiles, debouncedSearchTerm]);

  const downloadFile = useCallback(
    async (fileName: string) => {
      try {
        const file = await api.downloadFile(token, fileName);
        const url = window.URL.createObjectURL(new Blob([file]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        console.log(error);
        toast.error("Something went wrong, while trying to download");
      }
    },
    [token]
  );

  if (isLoading) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  }

  return (
    <div className="App">
      <div style={{ flex: "auto" }} className="container m-auto">
        <InputGroup className="my-md-3 my-1">
          <InputGroup.Text>
            <label htmlFor="search-files" title="Search files">
              <FontAwesomeIcon icon={faSearch} />
            </label>
          </InputGroup.Text>
          <Form.Control
            id="search-files"
            type="text"
            placeholder="Search files"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
        {files && files.length > 0 ? (
          <Table striped bordered>
            <thead>
              <tr>
                <th>File Name</th>
                <th>File Type</th>
                <th>File Size</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file, index) => {
                return (
                  <tr key={file.fileName}>
                    <td className="font-monospace">
                      {file.fileName}{" "}
                      {!searchTerm && index === 0 && (
                        <Badge bg="light" text="dark">
                          Active
                        </Badge>
                      )}
                    </td>
                    <td>{file.fileType}</td>
                    <td>{humanFileSize(file.fileSize)}</td>
                    <td className="text-center">
                      <Form.Group>
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={false}
                          onClick={() => downloadFile(file.fileName)}
                        >
                          <FontAwesomeIcon
                            icon={faDownload}
                            className="me-2"
                            aria-hidden="true"
                          />{" "}
                          Download
                        </Button>
                      </Form.Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : errorMessage ? (
          <ErrorMessage message={errorMessage} />
        ) : (
          <div style={{ width: "70vw" }}>
            <h1>No data available!</h1>
          </div>
        )}
      </div>
    </div>
  );
}

/** Helper HOC to wrap a component with authorization \
 * @param Component The component to wrap
 * @param userTypes The user types that are allowed to access the component
 * @returns A new component that checks authorization before rendering the wrapped component
 */
function withAuthorization<P extends object>(
  userTypes: UserType[],
  Component: React.ComponentType<{ token: string; userId: string }>,
  UnathorizedComponent: React.ComponentType<AuthorizationHookResult>
): React.FC<P> {
  return function AuthorizedWrapper(props: P) {
    return (
      <AuthorizedComponent
        userTypes={userTypes}
        element={Component}
        unauthorizedElement={UnathorizedComponent}
        {...props}
      />
    );
  };
}

export default withAuthorization(["User"], UserHome, AuthorizationErrorMessage);
